import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { FileTypeEnum } from '@repo/toolkit/types'

import { PdfIcon } from '../../../../icons'
import Typography from '../../typography'

// extensions each preview renderer can actually display
const PREVIEWABLE_EXTENSIONS: Partial<Record<FileTypeEnum, string[]>> = {
  [FileTypeEnum.PDF]: ['pdf'],
  [FileTypeEnum.IMAGE]: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'svg', 'bmp'],
  [FileTypeEnum.VIDEO]: ['mp4', 'webm', 'ogg', 'mov']
}

// files stored before the upload type check may not match their type (e.g. a word document typed PDF)
function isPreviewSupported(src: string, type: FileTypeEnum) {
  const supported = PREVIEWABLE_EXTENSIONS[type]
  if (!supported || src.startsWith('blob:')) return true
  const extension = src.split('?')[0].split('/').pop()?.split('.')[1]?.toLowerCase()
  return !extension || supported.includes(extension)
}

interface DisplayImage {
  src: string
  type: FileTypeEnum
  className?: string
  allowAll?: boolean
  onImagePreview?: () => void
}

export function DisplayImage({ allowAll, src, className, type, onImagePreview }: DisplayImage) {
  if (allowAll && !isPreviewSupported(src, type)) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-2', className)}>
        <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
          L'aperçu de ce fichier n'est pas pris en charge.
        </Typography>
        <a href={src} target="_blank" rel="noreferrer" className="text-primary underline text-sm">
          Ouvrir le fichier
        </a>
      </div>
    )
  }

  switch (type) {
    case FileTypeEnum.PDF:
      return (
        <div
          className={cn('flex justify-center items-center cursor-pointer rounded', className)}
          onMouseOver={onImagePreview}
          onClick={onImagePreview}
        >
          {allowAll ? (
            <iframe src={src} className="w-full h-full rounded overflow-hidden" />
          ) : (
            <PdfIcon
              className={'w-10 h-10'}
              color={tailwindColors.primary.DEFAULT}
              onClick={onImagePreview}
            />
          )}
        </div>
      )

    case FileTypeEnum.IMAGE:
      return (
        <img
          src={src}
          className={className}
          onMouseOver={onImagePreview}
          onClick={onImagePreview}
        />
      )
    case FileTypeEnum.VIDEO:
      return (
        <video
          className={className}
          controls
          onMouseOver={onImagePreview}
          onClick={onImagePreview}
        >
          <source src={src} type={'video/mp4'} />
          Your browser does not support the video tag.
        </video>
      )
    case FileTypeEnum.XML:
      return <img src={'/xml-logo.png'} className={className} onMouseOver={onImagePreview} />
    case FileTypeEnum.SHEET:
      return <img src={'/sheet-logo.png'} className={className} onMouseOver={onImagePreview} />
    default:
      return (
        <object
          data={src}
          className={className}
          onMouseOver={onImagePreview}
          onClick={onImagePreview}
        />
      )
  }
}
